/**
 * Browser Sentry for pos-web. No-ops without VITE_SENTRY_DSN.
 */
import * as Sentry from '@sentry/react';
import {
  scrubSentryEvent,
  type ScrubbableEvent,
} from './sentry-scrub.js';

let initialized = false;

export function isSentryEnabled(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

export function initSentry(): void {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  const isProd = import.meta.env.PROD;
  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    (import.meta.env.MODE as string) ||
    'development';

  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ||
    (import.meta.env.VITE_GIT_SHA as string | undefined) ||
    undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    sampleRate: 1.0,
    tracesSampleRate: isProd ? 0.1 : 0,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      const scrubbed = scrubSentryEvent(event as unknown as ScrubbableEvent);
      return scrubbed as unknown as typeof event;
    },
  });

  initialized = true;
}

/**
 * Tag authenticated staff user (id only) after login — never name/email.
 */
export function setSentryUser(user: {
  id: string;
  tenantId?: string;
  storeId?: string;
} | null): void {
  if (!isSentryEnabled()) return;
  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag('tenant_id', '');
    Sentry.setTag('store_id', '');
    return;
  }
  Sentry.setUser({ id: user.id });
  if (user.tenantId) Sentry.setTag('tenant_id', user.tenantId);
  if (user.storeId) Sentry.setTag('store_id', user.storeId);
}

export function captureReactError(
  error: unknown,
  info?: { componentStack?: string | null | undefined },
): void {
  if (!isSentryEnabled()) return;
  Sentry.withScope((scope) => {
    if (info?.componentStack) {
      scope.setContext('react', { componentStack: info.componentStack });
    }
    Sentry.captureException(error);
  });
}

export { Sentry };
